import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LandiStorefrontElement } from '../base/landi-storefront-element.js';
import {
  getProductImageDisplay,
  type ProductPlaceholderTheme,
} from '@landi/store-core';

/**
 * Product image with periodic-table placeholder fallback for missing/broken URLs.
 *
 * @element landi-product-image
 */
@customElement('landi-product-image')
export class LandiProductImage extends LandiStorefrontElement {
  static styles = css`
    :host {
      display: block;
    }

    :host([fill]) {
      width: 100%;
      height: 100%;
    }

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: var(--landi-product-image-fit, cover);
      object-position: center;
    }
  `;

  @property({ type: String, attribute: 'image-url' }) imageUrl = '';
  @property({ type: String, attribute: 'product-name' }) productName = '';
  @property({ type: String, attribute: 'product-sku' }) productSku = '';
  @property({ type: String, attribute: 'product-category' }) productCategory = '';
  @property({ type: String }) alt = '';
  @property({ type: String }) theme: ProductPlaceholderTheme = 'light';
  @property({ type: String }) loading: '' | 'lazy' | 'eager' = 'lazy';
  @property({ type: String, attribute: 'img-class' }) imgClass = '';
  /** When set, host stretches to fill a fixed-aspect image well (registry cards). */
  @property({ type: Boolean, reflect: true }) fill = false;
  /** CSS object-fit value — cover for photo wells, contain for periodic placeholders. */
  @property({ type: String }) fit: 'cover' | 'contain' = 'cover';

  render() {
    const display = getProductImageDisplay({
      url: this.imageUrl,
      name: this.productName,
      sku: this.productSku,
      category: this.productCategory,
      theme: this.theme,
    });

    return html`
      <img
        class=${this.imgClass}
        style=${`--landi-product-image-fit: ${this.fit}`}
        src=${display.src}
        alt=${this.alt || this.productName}
        loading=${this.loading}
        .onerror=${display.onError}
      />
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-image': LandiProductImage;
  }
}
